/* eslint-disable */
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const getDateRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

const groupOrdersByDate = (orders, startDate, endDate) => {
  const allDates = getDateRange(startDate, endDate);
  const dateFormatOptions = {
    day: "2-digit",
    month: "2-digit",
  };

  const groupedOrders = allDates.reduce((acc, date) => {
    const dateStr = date.toLocaleDateString("fr-FR", dateFormatOptions);
    acc[dateStr] = { confirmed: 0, total: 0 };
    return acc;
  }, {});

  orders.forEach((order) => {
    if (order.timeStamp?.seconds) {
      const orderDate = new Date(order.timeStamp.seconds * 1000);
      const orderDateStr = orderDate.toLocaleDateString(
        "fr-FR",
        dateFormatOptions
      );

      if (orderDate >= startDate && orderDate <= endDate) {
        if (!groupedOrders[orderDateStr]) {
          groupedOrders[orderDateStr] = { confirmed: 0, total: 0 };
        }

        groupedOrders[orderDateStr].total += 1;

        // Confirmée si status 3 ou 9
        if (order.deleveryStatus === "3" || order.deleveryStatus === "9") {
          groupedOrders[orderDateStr].confirmed += 1;
        }
      }
    }
  });

  const sortedEntries = Object.entries(groupedOrders).sort(
    ([dateA], [dateB]) => {
      const [dayA, monthA] = dateA.split("/").map(Number);
      const [dayB, monthB] = dateB.split("/").map(Number);
      return new Date(2024, monthA - 1, dayA) - new Date(2024, monthB - 1, dayB);
    }
  );

  return sortedEntries;
};

const StatisticsChart = ({ orders, dateRange, startDate, endDate }) => {
  const { t, i18n } = useTranslation();
  const isDark = document.documentElement.classList.contains("dark");

  // Calculer les dates
  const now = new Date();
  let start = new Date(startDate || now);
  let end = new Date(endDate || now);

  const groupedData = groupOrdersByDate(orders, start, end);

  const labels = groupedData.map(([date]) => date);
  const confirmationRates = groupedData.map(([, data]) => {
    return data.total === 0 ? 0 : Math.round((data.confirmed / data.total) * 100);
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: t("stats.confirmation_rate") || "Taux de Confirmation (%)",
        data: confirmationRates,
        borderColor: "#9b59bf",
        backgroundColor: "rgba(155, 89, 191, 0.1)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#9b59bf",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: isDark ? "#d1d5db" : "#374151",
          font: {
            size: 12,
            weight: "600",
          },
          boxWidth: 12,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: isDark ? "#1f2937" : "#fff",
        titleColor: isDark ? "#f3f4f6" : "#111827",
        bodyColor: isDark ? "#d1d5db" : "#374151",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return `${context.parsed.y}%`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          color: isDark ? "#9ca3af" : "#6b7280",
          font: {
            size: 11,
          },
          callback: function (value) {
            return value + "%";
          },
        },
        grid: {
          color: isDark ? "#374151" : "#e5e7eb",
          drawBorder: false,
        },
      },
      x: {
        ticks: {
          color: isDark ? "#9ca3af" : "#6b7280",
          font: {
            size: 11,
          },
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  };

  return (
    <div className="w-full bg-white dark:!bg-[#181919] rounded-xl p-6 border border-purple-100 dark:!border-[#3c3a3d]">
      <h3 className="text-lg font-semibold text-gray-800 dark:!text-gray-200 mb-4">
        {t("stats.confirmation_by_date") || t("stats.confirmation_by_date")}
      </h3>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default StatisticsChart;
